import { getSourceMapper } from '../../sources';

type AvitoStatus = {
  id?: string;
  name?: string;
  title?: string;
  description?: string;
  slug?: string;
  value?: string;
};

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string' && typeof value !== 'number') return null;
  const normalized = String(value).trim();
  return normalized || null;
}

function normalizeLabel(value: unknown): string {
  return String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLocaleLowerCase('ru-RU');
}

export function getAvitoApplicationId(response: any): string | null {
  return normalizeString(response?.id)
    || normalizeString(response?.application_id)
    || normalizeString(response?.applicationId);
}

export function getAvitoResumeId(response: any): string | null {
  return normalizeString(response?.resume?.id)
    || normalizeString(response?.resume_id)
    || normalizeString(response?.applicant?.resume_id);
}

export function getAvitoChatId(response: any): string | null {
  return normalizeString(response?.contacts?.chat?.value);
}

export function getAvitoPhone(response: any): string | null {
  return normalizeString(response?.contacts?.phones?.[0]?.value);
}

export function buildAvitoResponseCandidateSeed(response: any, candidateOverrides: any = {}) {
  const resume = response?.resume || {};
  const resumeId = getAvitoResumeId(response);
  const applicationId = getAvitoApplicationId(response);
  const fullName = normalizeString(candidateOverrides.fullName)
    || normalizeString(resume.fullName)
    || normalizeString(resume.full_name)
    || normalizeString(resume.name)
    || normalizeString(resume.title)
    || 'Кандидат';

  return {
    id: resumeId,
    source: 'avito',
    candidateSource: 'AVITO',
    applicationId,
    title: normalizeString(resume.title),
    name: fullName,
    fullName,
    firstName: normalizeString(candidateOverrides.firstName) || normalizeString(resume.firstName) || normalizeString(resume.first_name),
    lastName: normalizeString(candidateOverrides.lastName) || normalizeString(resume.lastName) || normalizeString(resume.last_name),
    middleName: normalizeString(candidateOverrides.middleName) || normalizeString(resume.middleName) || normalizeString(resume.middle_name),
    email: normalizeString(candidateOverrides.email) || normalizeString(response?.email) || normalizeString(resume.email),
    phone: normalizeString(candidateOverrides.phone) || getAvitoPhone(response) || normalizeString(resume.phone),
    avitoChatId: normalizeString(candidateOverrides.avitoChatId) || getAvitoChatId(response),
    telegram: normalizeString(candidateOverrides.telegram),
    whatsapp: normalizeString(candidateOverrides.whatsapp),
    resumeUrl: normalizeString(candidateOverrides.resumeUrl)
      || normalizeString(resume.resumeUrl)
      || normalizeString(resume.url)
      || normalizeString(resume.alternate_url),
    resumeBrief: normalizeString(resume.title),
    photoUrl: normalizeString(resume.photoUrl) || normalizeString(resume.photo),
  };
}

export function buildAvitoResponseCampaignCandidates(responses: any[]) {
  const mapper = getSourceMapper('avito');
  return responses.map((response) => mapper(buildAvitoResponseCandidateSeed(response)));
}

export function buildAvitoResponseCampaignCandidate(response: any, candidateOverrides: any = {}) {
  const mapper = getSourceMapper('avito');
  return mapper(buildAvitoResponseCandidateSeed(response, candidateOverrides));
}

export function buildAvitoResponsesCampaignData(params: {
  candidates: any[];
  selectedVacancy: any;
  positionId?: string | number | null;
  recruiterId?: string;
  campaignName: string;
  domain?: string;
}) {
  const vacancy = params.selectedVacancy || {};
  const vacancyTitle = normalizeString(vacancy.title) || 'Вакансия';
  const description = normalizeString(vacancy.description) || '';
  const companyDescription = normalizeString(vacancy.companyDescription) || '';
  const accountSettings = {
    telegramAccountId: null,
    whatsappAccountId: null,
    maxAccountId: null,
    messengerPriority: ['AVITO'],
    enabledMessengers: ['AVITO'],
  };

  return {
    name: params.campaignName,
    positionName: vacancyTitle,
    targetPosition: vacancyTitle,
    description,
    requirements: description,
    benefits: normalizeString(vacancy.socialPackageText) || '',
    companyDescription,
    teamDescription: '',
    candidateSource: 'AVITO',
    recruiterName: '',
    recruiterId: params.recruiterId || undefined,
    aiScreeningEnabled: Boolean(vacancy.aiScreeningEnabled),
    hrInterviewEnabled: vacancy.hrInterviewEnabled !== false,
    domain: params.domain,
    accountSettings,
    searchFilters: {
      source: 'avito',
      context: 'responses',
      avitoVacancyId: normalizeString(vacancy.avitoVacancyId),
    },
    status: 'DRAFT',
    positionId: params.positionId || null,
    avitoVacancyId: normalizeString(vacancy.avitoVacancyId) || undefined,
    attempts: 3,
    maxActiveDialogs: Math.max(50, params.candidates.length),
    dailyLimit: Math.max(20, params.candidates.length),
    pauseBetweenAttemptsHours: 6,
    contactWindowStart: 10,
    contactWindowEnd: 22,
    candidates: params.candidates,
  };
}

export function findAvitoMaybeSuitableStatus(states: AvitoStatus[]): string | null {
  const list = Array.isArray(states) ? states : [];
  const exact = list.find((state) => normalizeLabel(state.name || state.title || state.description) === 'может подойти');
  if (exact) {
    return normalizeString(exact.id) || normalizeString(exact.slug) || normalizeString(exact.value);
  }
  return null;
}
